import { useAppStore } from '@/stores/useAppStore';
import { useEmployeeStore } from '@/stores/useEmployeeStore';
import { useGroupAdminStore } from '@/stores/useGroupAdminStore';
import { useOrganizationStore } from '@/stores/useOrganizationStore';
import { useEducatorStore } from '@/stores/useEducatorStore';
import { useMainStore } from '@/stores/useMainStore';
import { roles } from '@/constants';
import { getCookie, setCookie } from '@/utils/cookies';
import getToken from '@/utils/getToken';
import {
    deriveAuth0Role,
    extractGroupAdminMetadata,
    extractEmployeeMetadata,
    isVolunteerAnAdmin,
    deriveEducatorRole,
    extractFirstOrgId,
} from '@/utils/auth/userMetadata';
import { hasCompletedOnboarding, isOrgAdminActive, needsDeactivation } from '@/utils/auth/agreements';
import envFlags from '@/data/flags/env_flags.json';

import * as educatorApi from '@/api/educators';
const groupAdminHandler = (userMetadata, appStore, groupAdminStore) => {
    const { groupAdminId, groups } = extractGroupAdminMetadata(userMetadata);

    groupAdminStore.setUser({ group_admin_id: groupAdminId, groups });

    setCookie('careers-app-mode', roles.GROUPADMIN_ROLE);
    appStore.updateApp({ active: true });
};

/**
 * Handler will set up org admin specific side-effects and orchestrates
 * whether the session selected is the volunteer/org-admin account (since org admins
 * are both volunteer and admin).
 */
const orgAdminHandler = (employeeStore, organizationStore, appStore, rememberedRole) => {
    employeeStore.updateUser({ hasAdmin: true });

    const validOrgAdminRoles = [roles.EMPLOYEE_ROLE, roles.ORGADMIN_ROLE];

    // Newly logged in user will be automatically brought to org admin page
    if (!validOrgAdminRoles.includes(rememberedRole)) {
        appStore.updateMode(roles.ORGADMIN_ROLE);
        setCookie('careers-app-mode', roles.ORGADMIN_ROLE);
    }

    // Org Admins shouldn't be able to access their volunteer account
    // if the org is inactive.
    const isOrgActive = !!organizationStore.organization?.isActive;
    if (!isOrgActive && rememberedRole === roles.EMPLOYEE_ROLE) {
        appStore.updateMode(roles.ORGADMIN_ROLE);
        setCookie('careers-app-mode', roles.ORGADMIN_ROLE, 0);
    }

    appStore.updateApp({
        active: isOrgAdminActive({
            isVolunteerMode: appStore.mode === roles.EMPLOYEE_ROLE,
            isOrgActive,
            employee: employeeStore.user.data,
        }),
    });
};

/**
 * Orchestrates store side effects involved with a user session (via Auth0/DE Auth metadata).
 */
export function useSession() {
    const appStore = useAppStore();
    const employeeStore = useEmployeeStore();
    const groupAdminStore = useGroupAdminStore();
    const organizationStore = useOrganizationStore();
    const educatorStore = useEducatorStore();
    const mainStore = useMainStore();

    async function resolveAuth0Session(authService) {
        if (!authService?.user) return;
        const { user_metadata: userMetadata } = authService.user;

        const rememberedRole = getCookie('careers-app-mode');
        const role = deriveAuth0Role(userMetadata, rememberedRole);

        if (!role) {
            authService.logout();
            return;
        }

        appStore.updateMode(role);

        if (role === roles.GROUPADMIN_ROLE) {
            groupAdminHandler(userMetadata, appStore, groupAdminStore);
            return;
        }

        if (role === roles.DE_ROLE) {
            appStore.updateApp({ active: true });
            return;
        }

        const { employeeId, organizations, parsedOrganizations } = extractEmployeeMetadata(userMetadata);

        // A user can theoretically be a part of multiple organizations.
        // But, BE limits this capability. Assume auth0 metadata for organizations
        // is a singleton.
        const volunteersOrgId = extractFirstOrgId(organizations);

        // Employee and Organization store data may already be populated from an earlier call
        // (e.g. main.js resolving the session at boot).
        // This avoids re-fetching on every subsequent routeGuide pass.
        if (!employeeStore.userId) {
            employeeStore.updateUser({ employeeId, organizations: parsedOrganizations });

            const token = await getToken(authService);
            await Promise.all([employeeStore.getEmployee({ token }), organizationStore.getOrganization({ token, organizationId: volunteersOrgId })]);
        }

        const isAdmin = isVolunteerAnAdmin(organizations, volunteersOrgId);
        // The admin's own employee record needs the same agreement check as any volunteer.
        if (needsDeactivation(employeeStore.user.data)) {
            appStore.updateApp({ returningToOnboarding: true });
            await employeeStore.deactivateEmployee({ token: await getToken(authService) });
        }

        if (isAdmin) orgAdminHandler(employeeStore, organizationStore, appStore, rememberedRole);
        else appStore.updateApp({ active: hasCompletedOnboarding(employeeStore.user.data) });
    }

    async function resolveEducatorSession(deAuthInstance) {
        const token = await deAuthInstance.getTokenSilently();

        if (deAuthInstance.error) {
            const errorMessage = deAuthInstance.error instanceof Error ? deAuthInstance.error.message : deAuthInstance.error;
            appStore.updateError({
                title: 'Authentication error',
                message: errorMessage,
            });
            return;
        }

        // Don't refetch metadata if store data is already populated.
        if (mainStore.user.data?.id) return;

        // Educator store function modifies the mainStore state...strange
        // but we are keeping to this for now.
        await educatorStore.getDEUser({ token });

        if (appStore.error) return;

        const { roles: userRoles, id: educatorId, site } = mainStore.user.data;

        appStore.updateMode(deriveEducatorRole(userRoles));

        await educatorStore.getEducator({ token, data: { educatorId } });

        // We fetch Educator details from BE and Auth0. But BE holds more metadata, such as if
        // the user accepted T&Cs.
        // We can use this to drive if we need to create an Educator record in BE and trigger onboarding.
        const educatorHasBackendRecord = 'termsChecked' in mainStore.user.data;

        if (!educatorHasBackendRecord) {
            const deUserData = mainStore.user.data;
            const result = await educatorApi.createEducator(deUserData);

            await educatorStore.storeEducatorData(result);
        }

        if (needsDeactivation(mainStore.user.data)) {
            appStore.updateApp({ returningToOnboarding: true });
            await educatorStore.updateEducator({ token, data: { isActive: false } });
        }

        if (site?.id) {
            await educatorStore.getSiteLocation(site.id);
        }

        // Env feature flags only need to be applied once, on first resolution.
        // Checked here (rather than by the caller comparing before/after state) because this is
        // the only place that actually knows whether this call was the first fetch - main.js and
        // authGuard.js both call resolveEducatorSession, so a caller-side before/after check can't
        // reliably tell "first resolution" apart from "someone else already resolved it".
        const env = import.meta.env.MODE;

        const flaggedEnv = Object.keys(envFlags).includes(env);
        if (flaggedEnv) {
            appStore.updateFeatureFlags({ flags: envFlags[env] });
        }
    }

    return { resolveAuth0Session, resolveEducatorSession };
}
